/** Maps Backend v2 transport DTOs to UI models; never invents fare/ETA/GPS/ratings. */
import type { components, paths } from '../../../../packages/contracts/src/generated/v2';
import type { Api } from './api';
import { endpoints as ep } from './endpoints';
import { ApiError } from './errors';
import type { HttpClient } from './http';
import type { ChangePlan, CommandState, Place, SupportTicket, Trip, TripState, User } from './types';

type DTO = components['schemas'];

/** GET /api/v2/demo/customer/trips/{id} — development facade only (not in the v2 contract). */
interface DemoTracking {
  trip_id: string;
  trip_status: string;
  location: { lat: number; lon: number; observed_at: string } | null;
  route: { type: 'LineString'; coordinates: [number, number][] };
  distance_m: number;
  duration_s: number;
  destination_name?: string;
  rerouted_around_flood: boolean;
}
type RouteDTO = paths['/api/v2/route-options/{route_option_id}']['get']['responses'][200]['content']['application/json']['data'];
type WirePage<T> = { items: T[]; next_cursor: string | null };
export interface LiveApiOptions {
  mode: 'live'; devUsername?: string; serviceAreaId?: string;
  /** Token phiên của /auth/login (dùng làm Bearer cho /api/v3 và SSE); null khi đăng xuất. */
  onSessionToken?: (token: string | null, username?: string) => void;
}
type SessionReply = { access_token?: string; username?: string };
type WireProposal = { proposal_id: string; version: number; content_version: number;
  proposal_digest: string; status: string; expires_at: string; rationale: string;
  action_type: string; payload: { trip_id: string; route_option_id?: string }; requirements: {
    kind: string; principal_id: string; satisfied: boolean }[] };
const states: Record<DTO['Trip']['status'], TripState> = {
  REQUESTED: 'MATCHING', CONFIRMED: 'MATCHING', DRIVER_ASSIGNED: 'ARRIVING',
  DRIVER_EN_ROUTE: 'ARRIVING', ARRIVED: 'ARRIVED', IN_PROGRESS: 'IN_PROGRESS',
  COMPLETED: 'COMPLETED', CANCELLED: 'CANCELLED',
};
const point = (p: { lat: number; lon: number }) => ({ lat: p.lat, lng: p.lon });
const address = (p: { lat: number; lon: number }) => `${p.lat.toFixed(5)}, ${p.lon.toFixed(5)}`;
const unsupported = (detail: string): never => { throw new ApiError(409, 'ACTION_UNAVAILABLE', detail); };
const money = (fare: DTO['Trip']['fare']) => {
  if (!fare) return undefined;
  if (fare.currency !== 'VND') throw new ApiError(502, 'UNSUPPORTED_CURRENCY', 'Giao diện chưa hỗ trợ loại tiền này');
  return fare.amount;
};

function tripView(t: DTO['Trip'], assigned?: DTO['AssignedDriver'] | null): Trip {
  return { id: t.trip_id, code: t.trip_id, state: states[t.status], productName: t.vehicle_type,
    createdAt: t.created_at, origin: { address: address(t.origin), point: point(t.origin) },
    destination: { address: address(t.destination), point: point(t.destination) },
    priceVnd: money(t.fare), paymentMethod: 'CASH_QR', allowedActions: [],
    driver: assigned ? { name: assigned.display_name, plate: assigned.plate_masked,
      vehicle: assigned.vehicle_label } : undefined };
}

export function createLiveApi(http: HttpClient, options: LiveApiOptions): Api {
  const get = async <T>(path: string, query?: Record<string, string | number | undefined>, signal?: AbortSignal) =>
    (await http.request<T>(path, { query, signal })).data;
  const me = (signal?: AbortSignal) => get<DTO['Me']>(ep.auth.me, undefined, signal);
  const area = async () => options.serviceAreaId ?? (await me()).service_area_ids[0]
    ?? unsupported('Tài khoản chưa được gán vùng phục vụ');
  const user = (p: DTO['Me']): User => ({ id: p.principal_id, name: p.display_name, phone: '', memberSince: '' });
  const places = new Map<string, Place>();
  const consents = new Map<string, { id: string; decision: 'ACCEPT' | 'DECLINE' }>();
  // Development tracking (AUTH_MODE=dev): canonical TripView has no live driver position or route
  // geometry. Merge them when the demo facade is mounted; one 404 turns the lookup off.
  let demoTracking = true;
  const tracking = async (id: string, signal?: AbortSignal) => {
    if (!demoTracking) return null;
    try {
      return await get<DemoTracking>(`/demo/customer/trips/${id}`, undefined, signal);
    } catch (e) {
      // Optional extra data: any failure keeps the canonical trip; a missing facade switches off.
      if (e instanceof ApiError && (e.status === 404 || e.status === 403) && e.code !== 'TRIP_NOT_FOUND') {
        demoTracking = false;
      }
      return null;
    }
  };
  const detail = async (id: string, signal?: AbortSignal) => {
    const view = await get<DTO['TripView']>(ep.trips.detail(id), undefined, signal);
    const trip: Trip = { ...tripView(view.trip, view.assigned_driver), pendingPlanId: view.active_proposal_ids?.[0] };
    const live = await tracking(id, signal);
    if (!live) return trip;
    return {
      ...trip,
      route: live.route,
      distanceKm: Math.round(live.distance_m / 100) / 10,
      durationMinutes: Math.ceil(live.duration_s / 60),
      destination: { ...trip.destination, address: live.destination_name ?? trip.destination.address },
      driver: trip.driver && live.location
        ? { ...trip.driver, location: { point: { lat: live.location.lat, lng: live.location.lon }, updatedAt: live.location.observed_at } }
        : trip.driver,
    };
  };
  const command = (d: { command_id: string; status: CommandState['status']; trip_id?: string | null }): CommandState =>
    ({ commandId: d.command_id, status: d.status, resourceId: d.trip_id ?? undefined });
  const transition = async (id: string, kind: string, key: string) => {
    const current = await get<DTO['TripView']>(ep.trips.detail(id));
    const response = await http.request<DTO['BookingCommand']>(ep.trips.transitions(id), {
      method: 'POST', body: { transition: kind, reason_code: 'CUSTOMER_REQUEST' },
      idempotencyKey: key, ifMatch: current.trip.version });
    return { ...response, data: { commandId: response.data.command_id, status: response.data.status } };
  };
  const plan = async (id: string, signal?: AbortSignal): Promise<ChangePlan> => {
    const [d, principal] = await Promise.all([get<WireProposal>(ep.proposals.detail(id), undefined, signal), me(signal)]);
    const requirement = d.requirements.find((r) => r.kind === 'CONSENT' && r.principal_id === principal.principal_id);
    const route = d.action_type === 'REROUTE' && d.payload.route_option_id ?
      await get<RouteDTO>(ep.routeOptions.detail(d.payload.route_option_id), undefined, signal) : undefined;
    const own = consents.get(id);
    const expired = new Date(d.expires_at).getTime() <= Date.now();
    const locked = ['EXECUTING', 'EXECUTED', 'SUPERSEDED', 'REJECTED'].includes(d.status);
    return { id: d.proposal_id, tripId: d.payload.trip_id, version: d.content_version,
      digest: d.proposal_digest, title: d.action_type, reason: d.rationale,
      changes: route ? [{ label: 'Tuyến thay thế', before: 'Chưa có số liệu tuyến hiện tại',
        after: `${(route.distance_m / 1000).toFixed(1)} km · ${(route.duration_s / 60).toFixed(1)} phút (${route.eta_basis})` }] : [],
      routeAfter: route?.geometry,
      expiresAt: d.expires_at,
      status: expired ? 'EXPIRED' : d.status === 'SUPERSEDED' ? 'WITHDRAWN' :
        d.status === 'REJECTED' || own?.decision === 'DECLINE' ? 'REJECTED' :
        requirement?.satisfied ? 'CONSENTED' : 'PENDING_CONSENT',
      executionStatus: d.status === 'EXECUTED' ? 'APPLIED' : d.status === 'EXECUTING' ? 'EXECUTING' : 'NOT_STARTED',
      allowedActions: expired || locked || !requirement || (d.action_type === 'REROUTE' && !route) ? [] : requirement.satisfied ?
        (own ? ['WITHDRAW'] : []) : own ? [] : ['CONSENT', 'REJECT'] };
  };
  const decide = async (id: string, digest: string, decision: 'ACCEPT' | 'DECLINE') => {
    const result = await http.request<{ consent_id: string }>(ep.proposals.consents(id), {
      method: 'POST', body: { decision, proposal_digest: digest } });
    consents.set(id, { id: result.data.consent_id, decision });
    return {};
  };
  const ticket = (d: DTO['SupportCase']): SupportTicket => ({ id: d.support_case_id,
    code: d.support_case_id, kind: d.severity === 'P0' ? 'SOS' : 'REQUEST',
    status: ['RESOLVED', 'CLOSED'].includes(d.status) ? 'RESOLVED' : d.status === 'OPEN' ? 'RECEIVED' : 'IN_PROGRESS',
    createdAt: d.created_at, history: [] });
  return {
    auth: {
      login: async (body) => {
        const username = body?.username || options.devUsername;
        if (!username) unsupported('Đăng nhập BFF cần tài khoản; chưa có đăng nhập bằng số điện thoại production');
        const reply = (await http.request<SessionReply>(ep.auth.login, { method: 'POST', auth: false,
          body: body?.password !== undefined ? { username, password: body.password } : { username } })).data;
        if (reply?.access_token) options.onSessionToken?.(reply.access_token, username);
        places.clear(); consents.clear();
        return { user: user(await me()) };
      },
      refresh: async () => {
        const reply = (await http.request<SessionReply>(ep.auth.refresh, { method: 'POST', auth: false })).data;
        if (reply?.access_token) options.onSessionToken?.(reply.access_token, undefined);
        return undefined;
      },
      me: async (signal) => user(await me(signal)),
      logout: async () => {
        await http.request(ep.auth.logout, { method: 'POST', auth: false });
        options.onSessionToken?.(null, undefined); places.clear(); consents.clear();
      },
    },
    places: { search: async (q, signal) => {
      const query = q.trim();
      if (query.length < 2) return [];
      const result = await get<WirePage<DTO['Place']>>(ep.places.search, { q: query, service_area_id: await area() }, signal);
      return result.items.map((d) => {
        const p: Place = { id: d.place_id, name: d.label, address: d.label,
          location: { lat: d.location.latitude, lng: d.location.longitude } };
        places.set(p.id, p); return p;
      });
    } },
    quotes: { create: async (body, signal) => {
      const destination = places.get(body.destinationPlaceId) ?? unsupported('Hãy tìm và chọn lại điểm đến để xác nhận tọa độ');
      const d = (await http.request<DTO['Quote']>(ep.quotes.create, { method: 'POST', signal,
        body: { origin: { lat: body.origin.lat, lon: body.origin.lng },
          destination: { lat: destination.location.lat, lon: destination.location.lng },
          service_area_id: await area(), vehicle_type: 'CAR', passenger_count: 1, wheelchair_required: false } })).data;
      const fare = money(d.fare)!;
      return { quoteId: d.quote_id, expiresAt: d.expires_at,
        origin: { address: address(d.origin), point: point(d.origin) },
        destination: { address: destination.address, point: point(d.destination) },
        options: [{ id: d.quote_id, tier: 'STANDARD', name: 'CAR', description: d.adapter_mode === 'SIMULATOR' ? 'Báo giá mô phỏng' : 'Báo giá hệ thống', priceVnd: fare }] };
    } },
    trips: {
      create: async (body, key) => {
        const r = await http.request<DTO['BookingCommand']>(ep.trips.create, {
          method: 'POST', body: { quote_id: body.quoteId }, idempotencyKey: key });
        return { status: r.status, commandId: r.data.command_id, tripId: r.data.trip_id ?? undefined };
      },
      list: async (p, signal) => {
        const result = await get<WirePage<DTO['Trip']>>(ep.trips.list, {
          service_area_id: await area(), cursor: p.cursor, limit: p.limit }, signal);
        return { items: result.items.map((d) => ({ id: d.trip_id, code: d.trip_id,
          state: states[d.status], productName: d.vehicle_type, startedAt: d.created_at,
          originLabel: address(d.origin), destinationLabel: address(d.destination), priceVnd: money(d.fare) })), nextCursor: result.next_cursor };
      },
      detail,
      timeline: async (id, signal) => (await get<WirePage<DTO['AuditItem']>>(ep.trips.timeline(id), undefined, signal)).items
        .map((d) => ({ at: d.occurred_at, label: d.event_type })),
      cancel: (id, key) => transition(id, 'CANCEL', key),
      confirm: (id, step, key) => transition(id, step === 'pickup' ? 'START' : 'COMPLETE', key),
      plans: async (id, signal) => {
        const view = await get<DTO['TripView']>(ep.trips.detail(id), undefined, signal);
        return Promise.all((view.active_proposal_ids ?? []).map((value) => plan(value, signal)));
      },
    },
    commands: { detail: async (id, signal) => command(await get(ep.commands.detail(id), undefined, signal)) },
    plans: {
      detail: plan, refresh: (id) => plan(id),
      consent: (id, body) => decide(id, body.digest, 'ACCEPT'),
      reject: async (id, body) => { await decide(id, body.digest, 'DECLINE'); },
      withdraw: async (id) => {
        const consent = consents.get(id) ?? unsupported('Cần tra consent của bạn trên Backend trước khi thu hồi; không thể dùng consent của người khác');
        await http.request(ep.proposals.revokeConsent(consent.id), { method: 'POST', body: { reason_code: 'CUSTOMER_REQUEST' } });
        consents.delete(id); return {};
      },
    },
    support: {
      sos: async (body, key) => ticket((await http.request<DTO['SupportCase']>(ep.support.sos, {
        method: 'POST', idempotencyKey: key, body: { trip_id: body.tripId ?? null,
          location: null, category: 'OTHER', note: body.note ?? '' } })).data),
      createRequest: async (body, key) => ticket((await http.request<DTO['SupportCase']>(ep.support.requests, {
        method: 'POST', idempotencyKey: key, body: { trip_id: body.tripId ?? null, incident_id: null,
          severity: 'P2', category: 'OTHER', note: body.message } })).data),
      list: async (signal) => (await get<WirePage<DTO['SupportCase']>>(ep.support.requests, undefined, signal)).items.map(ticket),
      detail: async (id, signal) => {
        const [d, events] = await Promise.all([get<DTO['SupportCase']>(ep.support.detail(id), undefined, signal),
          get<WirePage<DTO['SupportEvent']>>(ep.support.events(id), undefined, signal)]);
        return { ...ticket(d), history: events.items.map((e) => ({ at: e.created_at, label: e.note || e.type })) };
      },
    },
    home: { feed: async () => ({ ads: [], shops: [] }) },
    notifications: {
      list: async (p, signal) => {
        const d = await get<WirePage<DTO['Notification']>>(ep.notifications.list, { cursor: p.cursor }, signal);
        return { items: d.items.map((n) => ({ id: n.notification_id,
          category: n.resource_type === 'TRIP' ? 'TRIP' as const : n.resource_type === 'PROPOSAL' ? 'PLAN' as const : 'SYSTEM' as const,
          title: n.title, body: n.body, createdAt: n.created_at, seen: n.seen_at !== null,
          target: n.resource_type === 'TRIP' ? { type: 'TRIP' as const, id: n.resource_id } :
            n.resource_type === 'PROPOSAL' ? { type: 'PLAN' as const, id: n.resource_id } : undefined })), nextCursor: d.next_cursor };
      },
      markSeen: async (id) => { await http.request(ep.notifications.seen(id), { method: 'POST', idempotencyKey: crypto.randomUUID() }); },
    },
  };
}
